// Automated SEO audit and validation utilities

export interface SEOAuditResult {
  score: number;
  issues: SEOIssue[];
  recommendations: string[];
  passedChecks: string[];
  summary: {
    critical: number;
    warnings: number;
    passed: number;
    total: number;
  };
}

export interface SEOIssue {
  type: 'critical' | 'warning' | 'info';
  category: 'meta' | 'content' | 'technical' | 'performance' | 'mobile' | 'social';
  message: string;
  element?: string;
  fix?: string;
}

/**
 * Perform comprehensive automated SEO audit
 */
export const performSEOAudit = (document: Document = window.document): SEOAuditResult => {
  const issues: SEOIssue[] = [];
  const passedChecks: string[] = [];
  const recommendations: string[] = [];

  // Meta tags audit
  auditMetaTags(document, issues, passedChecks);
  
  // Content audit
  auditContent(document, issues, passedChecks);
  
  // Technical SEO audit
  auditTechnicalSEO(document, issues, passedChecks);
  
  // Performance audit
  auditPerformance(document, issues, passedChecks);
  
  // Mobile audit
  auditMobile(document, issues, passedChecks);
  
  // Social media audit
  auditSocialMedia(document, issues, passedChecks);
  
  // Generate recommendations
  generateRecommendations(issues, recommendations);
  
  // Calculate score
  const summary = {
    critical: issues.filter(i => i.type === 'critical').length,
    warnings: issues.filter(i => i.type === 'warning').length,
    passed: passedChecks.length,
    total: issues.length + passedChecks.length,
  };
  
  const score = calculateSEOScore(summary);
  
  return {
    score,
    issues,
    recommendations,
    passedChecks,
    summary,
  };
};

/**
 * Audit meta tags
 */
const auditMetaTags = (document: Document, issues: SEOIssue[], passedChecks: string[]): void => {
  // Title tag
  const title = document.querySelector('title');
  if (!title || !title.textContent?.trim()) {
    issues.push({
      type: 'critical',
      category: 'meta',
      message: 'Missing or empty title tag',
      fix: 'Add a descriptive title tag (50-60 characters)',
    });
  } else {
    const titleLength = title.textContent.length;
    if (titleLength < 30) {
      issues.push({
        type: 'warning',
        category: 'meta',
        message: `Title too short (${titleLength} characters)`,
        fix: 'Expand title to 50-60 characters for better SEO',
      });
    } else if (titleLength > 60) {
      issues.push({
        type: 'warning',
        category: 'meta',
        message: `Title too long (${titleLength} characters)`,
        fix: 'Shorten title to 50-60 characters',
      });
    } else {
      passedChecks.push('Title tag length is optimal');
    }
  }

  // Meta description
  const description = document.querySelector('meta[name="description"]');
  if (!description || !description.getAttribute('content')?.trim()) {
    issues.push({
      type: 'critical',
      category: 'meta',
      message: 'Missing meta description',
      fix: 'Add meta description (150-160 characters)',
    });
  } else {
    const descLength = description.getAttribute('content')!.length;
    if (descLength < 120) {
      issues.push({
        type: 'warning',
        category: 'meta',
        message: `Meta description too short (${descLength} characters)`,
        fix: 'Expand description to 150-160 characters',
      });
    } else if (descLength > 160) {
      issues.push({
        type: 'warning',
        category: 'meta',
        message: `Meta description too long (${descLength} characters)`,
        fix: 'Shorten description to 150-160 characters',
      });
    } else {
      passedChecks.push('Meta description length is optimal');
    }
  }

  // Canonical URL
  const canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    issues.push({
      type: 'warning',
      category: 'meta',
      message: 'Missing canonical URL',
      fix: 'Add canonical link tag',
    });
  } else {
    passedChecks.push('Canonical URL is present');
  }

  // Viewport meta tag
  const viewport = document.querySelector('meta[name="viewport"]');
  if (!viewport) {
    issues.push({
      type: 'critical',
      category: 'mobile',
      message: 'Missing viewport meta tag',
      fix: 'Add viewport meta tag for mobile optimization',
    });
  } else {
    passedChecks.push('Viewport meta tag is present');
  }
};

/**
 * Audit content
 */
const auditContent = (document: Document, issues: SEOIssue[], passedChecks: string[]): void => {
  // H1 tags
  const h1Tags = document.querySelectorAll('h1');
  if (h1Tags.length === 0) {
    issues.push({
      type: 'critical',
      category: 'content',
      message: 'Missing H1 tag',
      fix: 'Add one H1 tag with primary keyword',
    });
  } else if (h1Tags.length > 1) {
    issues.push({
      type: 'warning',
      category: 'content',
      message: `Multiple H1 tags found (${h1Tags.length})`,
      fix: 'Use only one H1 tag per page',
    });
  } else {
    passedChecks.push('Single H1 tag is present');
  }

  // Heading hierarchy
  const headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
  if (headings.length < 2) {
    issues.push({
      type: 'warning',
      category: 'content',
      message: 'Insufficient heading structure',
      fix: 'Add more headings (H2, H3) for better content structure',
    });
  } else {
    passedChecks.push('Good heading structure');
  }

  // Images without alt text
  const imagesWithoutAlt = document.querySelectorAll('img:not([alt])');
  if (imagesWithoutAlt.length > 0) {
    issues.push({
      type: 'warning',
      category: 'content',
      message: `${imagesWithoutAlt.length} images missing alt text`,
      fix: 'Add descriptive alt text to all images',
    });
  } else {
    const images = document.querySelectorAll('img');
    if (images.length > 0) {
      passedChecks.push('All images have alt text');
    }
  }

  // Content length
  const textContent = document.body.textContent || '';
  const wordCount = textContent.split(/\s+/).filter(word => word.length > 0).length;
  if (wordCount < 300) {
    issues.push({
      type: 'warning',
      category: 'content',
      message: `Content too short (${wordCount} words)`,
      fix: 'Add more content (aim for 300+ words)',
    });
  } else {
    passedChecks.push('Sufficient content length');
  }
};

/**
 * Audit technical SEO
 */
const auditTechnicalSEO = (document: Document, issues: SEOIssue[], passedChecks: string[]): void => {
  // Charset
  const charset = document.querySelector('meta[charset]');
  if (!charset) {
    issues.push({
      type: 'warning',
      category: 'technical',
      message: 'Missing charset declaration',
      fix: 'Add <meta charset="UTF-8"> to head',
    });
  } else {
    passedChecks.push('Charset is declared');
  }

  // Language attribute
  const html = document.documentElement;
  if (!html.getAttribute('lang')) {
    issues.push({
      type: 'warning',
      category: 'technical',
      message: 'Missing language attribute',
      fix: 'Add lang attribute to html tag',
    });
  } else {
    passedChecks.push('Language attribute is present');
  }

  // Structured data
  const structuredData = document.querySelectorAll('script[type="application/ld+json"]');
  if (structuredData.length === 0) {
    issues.push({
      type: 'warning',
      category: 'technical',
      message: 'No structured data found',
      fix: 'Add JSON-LD structured data',
    });
  } else {
    passedChecks.push('Structured data is present');
    
    // Validate JSON-LD
    structuredData.forEach((script, index) => {
      try {
        JSON.parse(script.textContent || '');
      } catch (e) {
        issues.push({
          type: 'critical',
          category: 'technical',
          message: `Invalid JSON-LD syntax in script ${index + 1}`,
          fix: 'Fix JSON syntax errors in structured data',
        });
      }
    });
  }

  // HTTPS check
  if (typeof window !== 'undefined' && window.location.protocol === 'http:') {
    issues.push({
      type: 'critical',
      category: 'technical',
      message: 'Site not using HTTPS',
      fix: 'Implement SSL certificate and redirect HTTP to HTTPS',
    });
  } else {
    passedChecks.push('Site uses HTTPS');
  }
};

/**
 * Audit performance
 */
const auditPerformance = (document: Document, issues: SEOIssue[], passedChecks: string[]): void => {
  // Large images
  const images = document.querySelectorAll('img');
  let largeImages = 0;
  images.forEach(img => {
    const src = img.getAttribute('src');
    if (src && !src.includes('webp') && !src.includes('avif')) {
      largeImages++;
    }
  });

  if (largeImages > 0) {
    issues.push({
      type: 'warning',
      category: 'performance',
      message: `${largeImages} images not optimized (use WebP/AVIF)`,
      fix: 'Convert images to modern formats (WebP, AVIF)',
    });
  } else if (images.length > 0) {
    passedChecks.push('Images are optimized');
  }

  // Lazy loading
  const imagesWithoutLazy = document.querySelectorAll('img:not([loading="lazy"])');
  if (imagesWithoutLazy.length > 3) {
    issues.push({
      type: 'info',
      category: 'performance',
      message: 'Consider lazy loading for images',
      fix: 'Add loading="lazy" to images below the fold',
    });
  }

  // External resources
  const externalScripts = document.querySelectorAll('script[src^="http"]');
  if (externalScripts.length > 5) {
    issues.push({
      type: 'info',
      category: 'performance',
      message: `Many external scripts (${externalScripts.length})`,
      fix: 'Minimize external scripts and use async/defer',
    });
  }
};

/**
 * Audit mobile optimization
 */
const auditMobile = (document: Document, issues: SEOIssue[], passedChecks: string[]): void => {
  // Touch targets
  const buttons = document.querySelectorAll('button, a, input[type="button"], input[type="submit"]');
  // This would need actual size calculation in a real implementation
  if (buttons.length > 0) {
    passedChecks.push('Interactive elements present');
  }

  // Font size check (simplified)
  const styles = document.querySelectorAll('style, link[rel="stylesheet"]');
  if (styles.length > 0) {
    passedChecks.push('Stylesheets present for mobile optimization');
  }
};

/**
 * Audit social media optimization
 */
const auditSocialMedia = (document: Document, issues: SEOIssue[], passedChecks: string[]): void => {
  // Open Graph tags
  const ogTitle = document.querySelector('meta[property="og:title"]');
  const ogDescription = document.querySelector('meta[property="og:description"]');
  const ogImage = document.querySelector('meta[property="og:image"]');

  if (!ogTitle || !ogDescription || !ogImage) {
    issues.push({
      type: 'warning',
      category: 'social',
      message: 'Incomplete Open Graph tags',
      fix: 'Add og:title, og:description, and og:image meta tags',
    });
  } else {
    passedChecks.push('Open Graph tags are complete');
  }

  // Twitter Card tags
  const twitterCard = document.querySelector('meta[name="twitter:card"]');
  if (!twitterCard) {
    issues.push({
      type: 'info',
      category: 'social',
      message: 'Missing Twitter Card tags',
      fix: 'Add Twitter Card meta tags',
    });
  } else {
    passedChecks.push('Twitter Card tags are present');
  }
};

/**
 * Generate recommendations based on issues
 */
const generateRecommendations = (issues: SEOIssue[], recommendations: string[]): void => {
  const criticalIssues = issues.filter(i => i.type === 'critical');
  const warningIssues = issues.filter(i => i.type === 'warning');

  if (criticalIssues.length > 0) {
    recommendations.push(`Fix ${criticalIssues.length} critical SEO issues immediately`);
  }

  if (warningIssues.length > 0) {
    recommendations.push(`Address ${warningIssues.length} SEO warnings for better optimization`);
  }

  // Category-specific recommendations
  const categories = new Set(issues.map(i => i.category));
  
  if (categories.has('meta')) {
    recommendations.push('Optimize meta tags for better search visibility');
  }
  
  if (categories.has('content')) {
    recommendations.push('Improve content structure and keyword optimization');
  }
  
  if (categories.has('technical')) {
    recommendations.push('Fix technical SEO issues for better crawlability');
  }
  
  if (categories.has('performance')) {
    recommendations.push('Optimize page performance for better user experience');
  }
  
  if (categories.has('mobile')) {
    recommendations.push('Enhance mobile optimization for mobile-first indexing');
  }
  
  if (categories.has('social')) {
    recommendations.push('Add social media meta tags for better sharing');
  }
};

/**
 * Calculate overall SEO score
 */
const calculateSEOScore = (summary: { critical: number; warnings: number; passed: number; total: number }): number => {
  const { critical, warnings, passed, total } = summary;
  
  if (total === 0) return 100;
  
  // Scoring: Critical issues -10 points, warnings -3 points, passed checks +2 points
  const score = Math.max(0, 100 - (critical * 10) - (warnings * 3) + (passed * 2));
  
  return Math.min(100, score);
};

/**
 * Auto-fix SEO issues where possible
 */
export const autoFixSEOIssues = (document: Document = window.document): string[] => {
  const fixes: string[] = [];

  // Fix missing charset
  if (!document.querySelector('meta[charset]')) {
    const charset = document.createElement('meta');
    charset.setAttribute('charset', 'UTF-8');
    document.head.insertBefore(charset, document.head.firstChild);
    fixes.push('Added missing charset meta tag');
  }

  // Fix missing viewport
  if (!document.querySelector('meta[name="viewport"]')) {
    const viewport = document.createElement('meta');
    viewport.name = 'viewport';
    viewport.content = 'width=device-width, initial-scale=1';
    document.head.appendChild(viewport);
    fixes.push('Added missing viewport meta tag');
  }

  // Fix missing lang attribute
  if (!document.documentElement.getAttribute('lang')) {
    document.documentElement.setAttribute('lang', 'en');
    fixes.push('Added language attribute to HTML tag');
  }

  // Fix images without alt text
  const imagesWithoutAlt = document.querySelectorAll('img:not([alt])');
  imagesWithoutAlt.forEach((img, index) => {
    const src = img.getAttribute('src') || '';
    const filename = src.split('/').pop()?.split('.')[0] || `image-${index + 1}`;
    img.setAttribute('alt', filename.replace(/[-_]/g, ' '));
    fixes.push(`Added alt text to image: ${filename}`);
  });

  // Add lazy loading to images
  const images = document.querySelectorAll('img:not([loading])');
  images.forEach((img, index) => {
    if (index > 2) { // Skip first 3 images (likely above fold)
      img.setAttribute('loading', 'lazy');
      fixes.push('Added lazy loading to image');
    }
  });

  return fixes;
};

/**
 * Generate SEO audit report
 */
export const generateSEOReport = (auditResult: SEOAuditResult): string => {
  const { score, issues, recommendations, passedChecks, summary } = auditResult;
  
  return `
# SEO Audit Report

## Overall Score: ${score}/100

### Summary
- ✅ Passed: ${summary.passed}
- ⚠️ Warnings: ${summary.warnings}  
- ❌ Critical: ${summary.critical}
- 📊 Total Checks: ${summary.total}

### Issues Found
${issues.map(issue => `
**${issue.type.toUpperCase()}** (${issue.category})
${issue.message}
${issue.fix ? `*Fix: ${issue.fix}*` : ''}
`).join('\n')}

### Recommendations
${recommendations.map(rec => `- ${rec}`).join('\n')}

### Passed Checks
${passedChecks.map(check => `✅ ${check}`).join('\n')}
`;
};